import { createMachine, assign } from 'xstate';
import { NARROW_WINDOW_WIDTH_PX } from './consts';

export type PlayerPositionManagerMachineEvent =
  | { type: 'INTERSECTION_OBSERVER_TARGET_START_INTERSECTING' }
  | { type: 'INTERSECTION_OBSERVER_TARGET_STOP_INTERSECTING' }
  | { type: 'PLAYER_PARENT_CONTAINER_RESIZE'; height: number }
  | { type: 'WINDOW_RESIZE' };

export type PlayerPositionManagerMachineContext = {
  intersectionObserverRootMarginTopPx: number;
  intersectionObserverTargetHeightPx: number;
  nonStickyPlayerPhantomHeightPx: number;
  playerAspect: number | null;
};

export const playerPositionManagerMachine = createMachine({
  types: {} as {
    events: PlayerPositionManagerMachineEvent;
    context: PlayerPositionManagerMachineContext;
  },
  context: {
    intersectionObserverRootMarginTopPx: 0,
    intersectionObserverTargetHeightPx: 0,
    nonStickyPlayerPhantomHeightPx: 0,
    playerAspect: null,
  },
  initial: 'notSticky',
  states: {
    notSticky: {
      entry: assign({
        nonStickyPlayerPhantomHeightPx: () => 0,
      }),
      on: {
        INTERSECTION_OBSERVER_TARGET_STOP_INTERSECTING: [
          {
            target: 'stickyAtBottomRight',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: ({ context }) =>
                  context.intersectionObserverTargetHeightPx,
              }),
              'makePlayerStickyAtBottomRight',
            ],
            guard: 'stickyAtBottomRightIsAllowed',
          },
          {
            target: 'stickyAtTop',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: ({ context }) =>
                  context.intersectionObserverTargetHeightPx,
              }),
              'makePlayerStickyAtTop',
            ],
            guard: 'stickyAtTopIsAllowed',
          },
        ],
        PLAYER_PARENT_CONTAINER_RESIZE: {
          actions: assign({
            intersectionObserverTargetHeightPx: ({ event }) => event.height,
            intersectionObserverRootMarginTopPx: ({ event }) => {
              if (window.innerWidth > NARROW_WINDOW_WIDTH_PX) {
                return 0;
              }
              return -event.height;
            },
          }),
        },
        WINDOW_RESIZE: [
          {
            target: 'stickyAtTop',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: ({ context }) =>
                  context.intersectionObserverTargetHeightPx,
              }),
              'makePlayerStickyAtTop',
            ],
            guard: 'stickyAtTopIsAllowed',
          },
          {
            target: 'stickyAtBottomRight',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: ({ context }) =>
                  context.intersectionObserverTargetHeightPx,
              }),
              'makePlayerStickyAtBottomRight',
            ],
            guard: 'stickyAtBottomRightIsAllowed',
          },
        ],
      },
    },
    stickyAtBottomRight: {
      entry: assign({
        nonStickyPlayerPhantomHeightPx: ({ context }) => context.intersectionObserverTargetHeightPx,
        intersectionObserverRootMarginTopPx: () => 0,
      }),
      on: {
        INTERSECTION_OBSERVER_TARGET_START_INTERSECTING: [
          {
            target: 'notSticky',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: () => 0,
              }),
              'makePlayerNotSticky',
            ],
          },
        ],
        WINDOW_RESIZE: [
          {
            target: 'stickyAtTop',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: ({ context }) =>
                  context.intersectionObserverTargetHeightPx,
              }),
              'makePlayerStickyAtTop',
            ],
            guard: 'stickyAtTopIsAllowed',
          },
          {
            target: 'stickyAtBottomRight',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: ({ context }) =>
                  context.intersectionObserverTargetHeightPx,
              }),
              'makePlayerStickyAtBottomRight',
            ],
            guard: 'stickyAtBottomRightIsAllowed',
          },
          {
            target: 'notSticky',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: () => 0,
              }),
              'makePlayerNotSticky',
            ],
          },
        ],
      },
    },
    stickyAtTop: {
      entry: assign({
        nonStickyPlayerPhantomHeightPx: ({ context }) => context.intersectionObserverTargetHeightPx,
      }),
      on: {
        INTERSECTION_OBSERVER_TARGET_START_INTERSECTING: [
          {
            target: 'notSticky',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: () => 0,
              }),
              'makePlayerNotSticky',
            ],
          },
        ],
        PLAYER_PARENT_CONTAINER_RESIZE: {
          actions: assign({
            intersectionObserverTargetHeightPx: ({ event }) => event.height,
            intersectionObserverRootMarginTopPx: ({ event }) => -event.height,
            nonStickyPlayerPhantomHeightPx: ({ event }) => event.height,
          }),
        },
        WINDOW_RESIZE: [
          {
            target: 'stickyAtBottomRight',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: ({ context }) =>
                  context.intersectionObserverTargetHeightPx,
              }),
              'makePlayerStickyAtBottomRight',
            ],
            guard: 'stickyAtBottomRightIsAllowed',
          },
          {
            target: 'stickyAtTop',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: ({ context }) =>
                  context.intersectionObserverTargetHeightPx,
              }),
              'makePlayerStickyAtTop',
            ],
            guard: 'stickyAtTopIsAllowed',
          },
          {
            target: 'notSticky',
            actions: [
              assign({
                nonStickyPlayerPhantomHeightPx: () => 0,
              }),
              'makePlayerNotSticky',
            ],
          },
        ],
      },
    },
  },
});
